import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Plus, FileText, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PROPOSAL_STATUS, PROPOSAL_STATUS_ORDER, GBP } from './constants';
import { useAuth } from '../../contexts/AuthContext';
import SortHeader from '../../components/SortHeader';
import BulkDeleteDialog from '../../components/BulkDeleteDialog';
import { useTableTools, useSortedItems } from '../../components/tableTools';

const SORT_ACCESSORS = {
    proposal_number: p => p.proposal_number || '',
    title:           p => (p.title || '').toLowerCase(),
    client_name:     p => (p.client_name || '').toLowerCase(),
    status:          p => p.status || '',
    option_count:    p => (p.options || []).length,
    year_one_net:    p => {
        const m = Math.min(...(p.options || []).map(o => o?.computed?.year_one_total_net ?? Infinity));
        return m === Infinity ? -1 : m;
    },
    valid_until:     p => p.valid_until || '',
    view_count:      p => p.public_view_count || 0,
    last_viewed:     p => p.public_last_viewed_at || '',
};

const fmtRelative = (iso) => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    if (Number.isNaN(diff)) return null;
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}d ago`;
    try {
        return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch { return null; }
};

export default function ProposalsList() {
    const navigate = useNavigate();
    const [sp, setSp] = useSearchParams();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showNew, setShowNew] = useState(sp.get('new') === '1');
    const { selected, toggleOne, toggleAll, clear, sortKey, sortDir, changeSort } = useTableTools({ defaultSortKey: 'proposal_number', defaultSortDir: 'desc' });
    const [confirmDelete, setConfirmDelete] = useState(null);
    const sortedItems = useSortedItems(items, sortKey, sortDir, SORT_ACCESSORS);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (statusFilter !== 'all') params.status = statusFilter;
            const r = await api.get('/proposals', { params });
            setItems(r.data.items);
            clear();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setLoading(false); }
    }, [search, statusFilter, clear]);

    useEffect(() => { load(); }, [load]);

    const openDeleteDialog = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        try {
            const r = await api.post('/proposals/bulk-delete-preview', { ids });
            setConfirmDelete({ counts: r.data.counts, names: r.data.sample_names, ids });
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
    };
    const doDelete = async (ids) => {
        try {
            const r = await api.post('/proposals/bulk-delete', { ids });
            const d = r.data.deleted || {};
            toast.success(`Deleted ${d.proposals || 0} proposal(s) + ${(d.contracts || 0) + (d.invoices || 0)} linked records`);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Delete failed'); }
    };

    return (
        <div data-testid="proposals-list-page" className="bm-fade-in">
            <PageHeader
                title="Proposals"
                description="Multi-option commercial proposals — drafted, sent, viewed, accepted."
                actions={
                    <div className="flex gap-2">
                        {isAdmin && selected.size > 0 && (
                            <Button onClick={openDeleteDialog} variant="outline" className="h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid="proposals-bulk-delete-btn">
                                <Trash2 size={15} className="mr-1.5" /> Delete {selected.size} selected
                            </Button>
                        )}
                        <Button onClick={() => setShowNew(true)} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="proposals-new-btn">
                            <Plus size={16} className="mr-1" /> New proposal
                        </Button>
                    </div>
                }
            >
                <div className="mt-5 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[240px] max-w-sm">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--bm-text-subtle)]" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by title, number, client…" className="pl-9 h-9 bg-white" data-testid="proposals-search-input" />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-9 w-[170px] bg-white" data-testid="proposals-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {PROPOSAL_STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{PROPOSAL_STATUS[s].label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </PageHeader>

            <div className="px-8 py-6">
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                    <table className="w-full text-[13px]">
                        <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                            <tr>
                                {isAdmin && (
                                    <th className="w-10 px-4 py-3">
                                        <input type="checkbox" checked={items.length > 0 && selected.size === items.length}
                                               onChange={() => toggleAll(items)} className="cursor-pointer"
                                               data-testid="proposals-select-all" aria-label="Select all proposals" />
                                    </th>
                                )}
                                <SortHeader label="Proposal"     sortKey="title"           currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                <SortHeader label="Client"       sortKey="client_name"     currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                <SortHeader label="Status"       sortKey="status"          currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                <SortHeader label="Options"      sortKey="option_count"    currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                                <SortHeader label="Views"        sortKey="view_count"      currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                                <SortHeader label="From Year-1"  sortKey="year_one_net"    currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                                <SortHeader label="Valid until"  sortKey="valid_until"     currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (<tr><td colSpan={isAdmin ? 8 : 7} className="px-5 py-10 text-center text-[color:var(--bm-text-muted)]">Loading…</td></tr>)}
                            {!loading && sortedItems.length === 0 && (
                                <tr>
                                    <td colSpan={isAdmin ? 8 : 7} className="px-5 py-14 text-center">
                                        <FileText size={36} className="mx-auto text-[color:var(--bm-text-subtle)]" />
                                        <div className="mt-3 text-[color:var(--bm-text-muted)] text-[14px]">No proposals yet.</div>
                                        <Button onClick={() => setShowNew(true)} className="mt-4 h-9" style={{ background: 'var(--bm-primary)' }} data-testid="proposals-empty-new-btn">
                                            <Plus size={16} className="mr-1" /> New proposal
                                        </Button>
                                    </td>
                                </tr>
                            )}
                            {sortedItems.map(p => {
                                const minY1 = Math.min(...(p.options || []).map(o => o?.computed?.year_one_total_net || Infinity));
                                const isSel = selected.has(p.id);
                                return (
                                    <tr key={p.id} onClick={() => navigate(`/proposals/${p.id}`)}
                                        className={`border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] cursor-pointer transition ${isSel ? 'bg-[color:var(--bm-accent-50)]/60' : ''}`}
                                        data-testid={`proposal-row-${p.id}`}>
                                        {isAdmin && (
                                            <td className="px-4 py-3.5" onClick={(e) => { e.stopPropagation(); toggleOne(p.id); }}>
                                                <input type="checkbox" checked={isSel} onChange={() => toggleOne(p.id)}
                                                       onClick={(e) => e.stopPropagation()} className="cursor-pointer"
                                                       data-testid={`proposal-select-${p.id}`} />
                                            </td>
                                        )}
                                        <td className="px-5 py-3.5">
                                            <div className="font-medium text-[color:var(--bm-text)]">{p.title}</div>
                                            <div className="text-[11.5px] text-[color:var(--bm-text-subtle)]">{p.proposal_number}</div>
                                        </td>
                                        <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{p.client_name}</td>
                                        <td className="px-4 py-3.5">
                                            <span className={`bm-pill ${PROPOSAL_STATUS[p.status]?.tone || ''} border`}>
                                                {PROPOSAL_STATUS[p.status]?.label || p.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3.5 text-right text-[color:var(--bm-text-muted)]">{(p.options || []).length}</td>
                                        <td className="px-4 py-3.5 text-right" data-testid={`proposal-views-${p.id}`}>
                                            <div className="font-medium text-[color:var(--bm-text)]">{p.public_view_count || 0}</div>
                                            <div className="text-[10.5px] text-[color:var(--bm-text-subtle)] mt-0.5" title={p.public_last_viewed_at ? new Date(p.public_last_viewed_at).toLocaleString('en-GB') : ''}>
                                                {p.public_last_viewed_at ? fmtRelative(p.public_last_viewed_at) : '—'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3.5 text-right font-medium text-[color:var(--bm-text)]">{minY1 === Infinity ? '—' : GBP(minY1)}</td>
                                        <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{p.valid_until || '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <NewProposalDialog open={showNew} onClose={() => { setShowNew(false); sp.delete('new'); setSp(sp); }} onCreated={(p) => navigate(`/proposals/${p.id}`)} preClientId={sp.get('client_id')} />

            <BulkDeleteDialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} info={confirmDelete}
                              entityLabel="proposal" onConfirm={doDelete} testId="proposals-bulk-delete-dialog" />
        </div>
    );
}

function NewProposalDialog({ open, onClose, onCreated, preClientId }) {
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState(preClientId || '');
    const [title, setTitle] = useState('');
    const [scope, setScope] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get('/clients', { params: { per_page: 200 } }).then(r => setClients(r.data.items));
        setClientId(preClientId || '');
    }, [open, preClientId]);

    const submit = async (e) => {
        e.preventDefault();
        if (!clientId) return toast.error('Please select a client');
        if (!title.trim()) return toast.error('Title is required');
        setSubmitting(true);
        try {
            const r = await api.post('/proposals', {
                client_id: clientId,
                title: title.trim(),
                scope_summary: scope.trim() || null,
                options: [
                    { name: 'Option A — Zero Setup', setup_fee: 0, monthly_saas_fee: 199, contract_length_months: 12, payment_frequency: 'monthly', discount_pct: 0, vat_applies: true },
                    { name: 'Option B — Setup + Lower Monthly', setup_fee: 1500, deposit_pct: 50, monthly_saas_fee: 129, contract_length_months: 12, minimum_commitment_months: 6, payment_frequency: 'monthly', discount_pct: 0, vat_applies: true },
                    { name: 'Option C — Annual Upfront', setup_fee: 500, monthly_saas_fee: 199, contract_length_months: 12, payment_frequency: 'annual_upfront', discount_pct: 10, vat_applies: true },
                ],
            });
            toast.success(`Proposal ${r.data.proposal_number} created`);
            onCreated(r.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-[520px]" data-testid="new-proposal-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-[20px]">New proposal</DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">Starts with 3 recommended options — Zero Setup, Setup + Lower Monthly, Annual Upfront. Customise next.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div>
                        <Label className="text-[12.5px] font-medium">Client *</Label>
                        <Select value={clientId} onValueChange={setClientId}>
                            <SelectTrigger className="mt-1.5 h-10" data-testid="new-proposal-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                            <SelectContent>
                                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[12.5px] font-medium">Proposal title *</Label>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Beyond Measure HQ rollout" className="mt-1.5 h-10" required data-testid="new-proposal-title-input" />
                    </div>
                    <div>
                        <Label className="text-[12.5px] font-medium">Scope summary</Label>
                        <textarea value={scope} onChange={(e) => setScope(e.target.value)} placeholder="One or two sentences describing what's in scope…" rows={3} className="mt-1.5 w-full rounded-md border border-[color:var(--bm-border)] p-3 text-[13px]" />
                    </div>
                    <DialogFooter className="pt-2">
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={submitting} style={{ background: 'var(--bm-primary)' }} data-testid="new-proposal-submit-btn">
                            {submitting ? 'Creating…' : 'Create proposal'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
